/** Bloquear horário (toque longo no livre): almoço, folga, compromisso. */
import { dayBoundsUtc, formatDayLong, formatHourOf, toIso, toMs, utcToZoned } from '@lindeza/core';
import { useLiveQuery, useProfessional, useRuntime } from '@lindeza/core/hooks';
import { useState } from 'react';
import { toastError, withUndo } from '../../app/ui';
import { IconBriefcase, IconEyeOff, IconLock, IconNotebook, IconTreePalm, IconUtensils } from '../../components/Icons';
import { Sheet } from '../../components/ui';

const REASONS = ['Almoço', 'Compromisso', 'Folga', 'Curso'];
const REASON_ICONS: Record<string, typeof IconLock> = { Almoço: IconUtensils, Compromisso: IconBriefcase, Folga: IconTreePalm, Curso: IconNotebook };
const DURATIONS = [30, 60, 90, 120];

export function BlockSheet({ start, blockId, onClose }: { start: string; blockId?: string; onClose: () => void }) {
  const rt = useRuntime();
  const prof = useProfessional();
  const tz = prof?.timezone ?? 'America/Sao_Paulo';
  const [reason, setReason] = useState('Almoço');
  const [busy, setBusy] = useState(false);
  const block = useLiveQuery(async (r) => (blockId ? r.db.get('block', blockId) : undefined), [blockId], ['block']);

  if (blockId && block) {
    return (
      <Sheet title={block.reason ?? 'Horário bloqueado'} sub={`${formatHourOf(block.start_utc, tz)}–${formatHourOf(block.end_utc, tz)}`} onClose={onClose}>
        <button
          className="btn btn--primary btn--lg btn--block"
          onClick={async () => {
            await rt.actions.unblock(block.id);
            onClose();
            withUndo('Horário liberado.', async () => {
              await rt.actions.blockTime(block.start_utc, block.end_utc, block.reason);
            });
          }}
        >
          Liberar este horário
        </button>
      </Sheet>
    );
  }

  const endOfDay = dayBoundsUtc(utcToZoned(start, tz).date, tz).end;

  async function save(minutes: number | 'day') {
    setBusy(true);
    try {
      const end = minutes === 'day' ? endOfDay : Math.min(endOfDay, toMs(start) + minutes * 60_000);
      const b = await rt.actions.blockTime(start, toIso(end), reason);
      onClose();
      withUndo(`${reason}: ${formatHourOf(b.start_utc, tz)}–${formatHourOf(b.end_utc, tz)} bloqueado.`, () => rt.actions.unblock(b.id));
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Bloquear horário" sub={`${formatDayLong(utcToZoned(start, tz).date).replace(/^./, (c) => c.toUpperCase())}, a partir de ${formatHourOf(start, tz)}`} onClose={onClose}>
      <div className="stack stack--lg">
        <div>
          <p className="at-sec-title" style={{ marginBottom: 6 }}>
            Motivo
          </p>
          <div className="chips chips--wrap" role="group" aria-label="Motivo">
            {REASONS.map((r) => {
              const Icon = REASON_ICONS[r];
              return (
                <button key={r} className="chip" aria-pressed={reason === r} onClick={() => setReason(r)}>
                  {Icon && <Icon />} {r}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <p className="at-sec-title" style={{ marginBottom: 6 }}>
            Por quanto tempo? Toque para bloquear
          </p>
          <div className="pay-grid">
            {DURATIONS.map((m) => (
              <button key={m} className="btn btn--outline btn--lg" disabled={busy} onClick={() => void save(m)}>
                {m < 60 ? `${m} min` : `${m / 60}h`.replace('.5h', 'h30')}
              </button>
            ))}
            <button className="btn btn--primary btn--lg" style={{ gridColumn: 'span 2' }} disabled={busy} onClick={() => void save('day')}>
              <IconLock /> Até o fim do dia
            </button>
          </div>
        </div>
        <p className="status">
          <IconEyeOff /> A cliente não vê esse horário no link.
        </p>
      </div>
    </Sheet>
  );
}
